﻿import type { ReservationState, RoomBookingState } from './types';

import { default as classNames } from "classnames";

import { format, differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';

import Modal from 'react-bootstrap/Modal';
import { getRoomTypeDisplayName } from './OrderDetailsComponent';
import { countCalendarDaysBetween, decimalToTHB, stringIsNullOrEmpty } from '@/utils/utils';
import { getBreakdownSourceLabel } from '@/utils/priceBreakdown';

export function RoomPriceDialogComponent({ reservationState, roomBookingState, visible, closed }: RoomPriceDialogComponentProps) {

    let baseTotal = 0;
    let payableTotal = 0;

    for (const roomDate of roomBookingState.Dates) {
        baseTotal += roomDate.CalculatedPrice.totalBaseAmount;
        payableTotal += roomDate.CalculatedPrice.totalPayableAmount;
    }

    const discounted = payableTotal < baseTotal;

    const nights = countCalendarDaysBetween(roomBookingState.CheckIn.value, roomBookingState.CheckOut.value) - 1;

    return (
        <Modal centered backdrop="static" show={visible} onHide={() => { closed?.(); }} size="lg" >
            <Modal.Header closeButton>
                <Modal.Title>Room Price</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <table className="table">
                    <tbody>
                        <tr>
                            <th colSpan={2}>
                                <div className="mb-2 text-lg">
                                    <div>{getRoomTypeDisplayName(reservationState, roomBookingState.RoomType.value)}</div>
                                    <div>{format(roomBookingState.CheckIn.value, 'dd/MM/yyyy')} - {format(roomBookingState.CheckOut.value, 'dd/MM/yyyy')} ({nights} nights)</div>
                                </div>
                            </th>
                        </tr>
                        {roomBookingState.Dates.map((roomDate, i) => {

                            if (roomDate.PriceBreakdown) {

                                return [
                                    (
                                        <tr key={`${roomDate.Date.getTime().toString()}_0`}>
                                            <th className={classNames({ 'pt-4': i > 0 }, "font-bold")} colSpan={2}>
                                                {format(roomDate.Date, 'dd/MM/yyyy')}
                                            </th>
                                        </tr>
                                    ),
                                    (
                                        <tr key={`${roomDate.Date.getTime().toString()}_1`}>
                                            <td>
                                                {getBreakdownSourceLabel(roomDate.PriceBreakdown)}
                                            </td>
                                            <td className="text-right">
                                                {decimalToTHB(roomDate.PriceBreakdown.BaseAmount.Amount.Gross)}
                                            </td>
                                        </tr>
                                    ),
                                    ...roomDate.PriceBreakdown.Multipliers.map((multiplier, j) => {
                                        return [(
                                            <tr key={`${roomDate.Date.getTime().toString()}_${j + 2}`}>
                                                <td>
                                                    {multiplier.Source} ({multiplier.Multiplier * 100}%)
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(multiplier.AdjustmentAmount.Gross)}
                                                </td>
                                            </tr>
                                        )];
                                    }),
                                    ...roomDate.PriceBreakdown.Discounts.map((discount, j) => {
                                        return [(
                                            <tr key={`${roomDate.Date.getTime().toString()}_${j + 2}`}>
                                                <td>
                                                    {discount.Source} ({discount.Multiplier * 100}%)
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(discount.AdjustmentAmount.Gross)}
                                                </td>
                                            </tr>
                                        )];
                                    }),
                                    ...roomDate.VoucherEffects.map((voucher, i) => {
                                        return [(
                                            <tr key={`${roomDate.Date.getTime().toString()}_${i + 2}`}>
                                                {voucher.Effects.map((e, j) => {
                                                    return [(
                                                        <td key={`${roomDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_1`}>
                                                            <div className="max-w-xs">
                                                                {voucher.Name}{(!stringIsNullOrEmpty(voucher.Code) ? ` (${voucher.Code})` : "")} - {e.Effect}
                                                            </div>
                                                        </td>
                                                    ),
                                                    (
                                                        <td className="text-right" key={`${roomDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_2`}>
                                                            {decimalToTHB(e.Amount * -1)}
                                                        </td>
                                                    )];
                                                })}
                                            </tr>
                                        )];
                                    }),
                                    ...roomDate.MinorAdjustment.value ? [
                                        (
                                            <tr key={`${roomDate.Date.getTime().toString()}_minoradjustment`} >
                                                <td>
                                                    Minor Adjustment
                                                </td>
                                                <td className="text-right">
                                                    {decimalToTHB(roomDate.MinorAdjustment.value)}
                                                </td>
                                            </tr>
                                        )
                                    ] : [],
                                    ...roomDate.AdditionalCharges.value.map((charge, i) => {
                                        return [(
                                            <tr key={`${roomDate.Date.getTime().toString()}_${i + 2}`}>
                                                <td key={`${roomDate.Date.getTime().toString()}_${i + 2}_Charges_1`}>
                                                    <div className="max-w-xs">
                                                        {getBreakdownSourceLabel(charge)}
                                                    </div>
                                                </td>
                                                <td className="text-right" key={`${roomDate.Date.getTime().toString()}_${i + 2}_Charges_2`}>
                                                    {charge.AmountAfterDiscount.Gross.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                                </td>
                                            </tr>
                                        )];
                                    }),
                                    <tr className="font-bold" key={`${roomDate.Date.getTime().toString()}_${roomDate.VoucherEffects.length + 3}`}>
                                        <td>
                                            Total
                                        </td>
                                        <td className="text-right">
                                            {roomDate.CalculatedPrice.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                ];
                            }

                            return [
                                (
                                    <tr key={`${roomDate.Date.getTime().toString()}_0`}>
                                        <th className={classNames({ 'pt-4': i > 0 }, "font-bold")} colSpan={2}>
                                            {format(roomDate.Date, 'dd/MM/yyyy')}
                                        </th>
                                    </tr>
                                ),
                                (roomDate.CalculatedPrice.totalBaseAmount > roomDate.CalculatedPrice.totalPayableAmount) && (
                                    <tr key={`${roomDate.Date.getTime().toString()}_1`}>
                                        <td>
                                            Base
                                        </td>
                                        <td className="text-right">
                                            {roomDate.CalculatedPrice.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                ),
                                ...roomDate.VoucherEffects.map((voucher, i) => {
                                    return [(
                                        <tr key={`${roomDate.Date.getTime().toString()}_${i + 2}`}>
                                            {voucher.Effects.map((e, j) => {
                                                return [(
                                                    <td key={`${roomDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_1`}>
                                                        <div className="max-w-xs">
                                                            {voucher.Name}{(!stringIsNullOrEmpty(voucher.Code) ? ` (${voucher.Code})` : "")} - {e.Effect}
                                                        </div>
                                                    </td>
                                                ),
                                                (
                                                    <td className="text-right" key={`${roomDate.Date.getTime().toString()}_${i + 2}_Effects_${j}_2`}>
                                                        {e.Amount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                                    </td>
                                                )];
                                            })}
                                        </tr>
                                    )];
                                }),
                                (
                                    <tr className="font-bold" key={`${roomDate.Date.getTime().toString()}_${roomDate.VoucherEffects.length + 3}`}>
                                        <td>
                                            Total
                                        </td>
                                        <td className="text-right">
                                            {roomDate.CalculatedPrice.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                        </td>
                                    </tr>
                                )];
                        })}
                        {discounted && (
                            <tr className="font-bold">
                                <td className="pt-4">
                                    Base Total
                                </td>
                                <td className="pt-4 text-right line-through">
                                    {baseTotal.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                                </td>
                            </tr>
                        )}
                        <tr className="font-bold">
                            <td className={classNames({ 'pt-4': !discounted })}>
                                {discounted ? "Payable Total" : "Total"}
                            </td>
                            <td className={classNames({ 'pt-4': !discounted }, "text-right")}>
                                {payableTotal.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Modal.Body>
        </Modal>
    );
}

export interface RoomPriceDialogComponentProps {
    reservationState: ReservationState;
    roomBookingState: RoomBookingState;
    visible: boolean;
    closed?: () => void;
}
